import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import Box from '@/Atoms/Box';
import Spacer from '@/Atoms/Spacer';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

const HIGH_RTT_THRESHOLD_MS = 300;
const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g', '3g'];

function readConnection(): any {
  const nav = navigator as any;
  return nav.connection || nav.mozConnection || nav.webkitConnection || null;
}

function readDeviceSnapshot() {
  const connection = readConnection();
  const cores = navigator.hardwareConcurrency || null;
  const ram = (navigator as any).deviceMemory ?? null;
  const effectiveType: string | null = connection?.effectiveType ?? null;
  const rtt: number | null =
    connection && typeof connection.rtt === 'number' ? connection.rtt : null;

  const lowHardware = (cores ?? 5) <= 4 || (ram ?? 6) <= 4;
  const slowType = effectiveType
    ? SLOW_EFFECTIVE_TYPES.includes(effectiveType)
    : false;
  const highRtt = rtt !== null && rtt > HIGH_RTT_THRESHOLD_MS;
  const isLowEndDevice = lowHardware || slowType || highRtt;

  return {cores, ram, effectiveType, rtt, isLowEndDevice};
}

type Props = {
  meetingRole: string;
};

type SpecRowProps = {label: string; value: string};

function SpecRow({label, value}: SpecRowProps) {
  return (
    <Box flexDirection="row" justifyContent="space-between">
      <CCText fontSize={FontSizes[13]} color={CCColors.Grey.Grey}>
        {label}
      </CCText>
      <CCText fontSize={FontSizes[13]} fontWeight="semibold">
        {value}
      </CCText>
    </Box>
  );
}

function CoachDeviceWarning({meetingRole}: Props) {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<ReturnType<
    typeof readDeviceSnapshot
  > | null>(null);

  useEffect(() => {
    console.log(
      `[CoachDeviceWarning] mounted with meetingRole="${meetingRole}"`,
    );
    if (meetingRole === 'student' || meetingRole === '') {
      console.log('[CoachDeviceWarning] skipped: meetingRole is "student"');
      return;
    }
    const snap = readDeviceSnapshot();
    console.log('[CoachDeviceWarning] snapshot:', snap);
    if (!snap.isLowEndDevice) {
      console.log('[CoachDeviceWarning] skipped: isLowEndDevice is false');
      return;
    }
    console.log('[CoachDeviceWarning] showing modal');
    setSnapshot(snap);
    setOpen(true);
  }, [meetingRole]);

  if (!snapshot) return null;

  const networkLabel = snapshot.effectiveType
    ? snapshot.effectiveType.toUpperCase()
    : 'unknown';
  const rttLabel =
    snapshot.rtt !== null ? `${snapshot.rtt} ms` : 'unavailable';
  const coresLabel = snapshot.cores !== null ? `${snapshot.cores}` : 'unknown';
  const ramLabel = snapshot.ram !== null ? `${snapshot.ram} GB` : 'unknown';

  return (
    <SlideModal
      type="center"
      modalHeaderTitle="Heads up, Coach"
      visible={open}
      onClose={() => setOpen(false)}>
      <Box margin={2}>
        <CCText fontSize={FontSizes[14]}>
          Your device or network may affect class quality. Here&apos;s what we
          detected:
        </CCText>
        <Spacer spacing={16} />
        <View>
          <SpecRow label="CPU cores" value={coresLabel} />
          <Spacer spacing={6} />
          <SpecRow label="RAM" value={ramLabel} />
          <Spacer spacing={6} />
          <SpecRow label="Network" value={networkLabel} />
          <Spacer spacing={6} />
          <SpecRow label="Latency (RTT)" value={rttLabel} />
        </View>
        <Spacer spacing={16} />
        <CCText fontSize={FontSizes[13]} color={CCColors.Grey.Grey}>
          Try closing other apps, plugging into ethernet, or moving closer to
          your router for a smoother class.
        </CCText>
        <Spacer spacing={20} />
        <Box type="row-flex-end">
          <CCButton onPress={() => setOpen(false)}>Got it</CCButton>
        </Box>
      </Box>
    </SlideModal>
  );
}

export default CoachDeviceWarning;
